import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import {
  useLazyDeleteByIdFormQuery,
  useLazyGetAllFormsQuery,
  useUpdateFormsMultiMutation,
  useUpdateFormsMutation,
} from "../../services/forum";
import { successToast, errorToast } from "../../helpers";
import Loader from "../../helpers/Loader";
import WarnModal from "../../components/modals/WarnModal";
import Pagination from "../../components/Pagination";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validation";
import {
  APP_ROLE,
  BULK_STATUS,
  CONTENT_TYPE,
} from "../../helpers/constants/enums";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { useGetTutorListQuery } from "../../services/class";

const TutorForm = () => {
  const [deleteById] = useLazyDeleteByIdFormQuery();
  const [allForms] = useLazyGetAllFormsQuery();
  const [updated] = useUpdateFormsMutation();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const totalPages = Math.ceil(totalCount / 10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const [rows, setRows] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [updatedMulti] = useUpdateFormsMultiMutation();
  const { data: tutorData } = useGetTutorListQuery();
  const [selectedTutor, setSelectedTutor] = useState("");

  const handleTutorChange = (event: any) => {
    setSelectedTutor(event.target.value);
  };

  const handleDeleteForum = async (id: any) => {
    try {
      const response = await deleteById({ id }).unwrap();
      if (response?.statusCode === 200) {
        successToast("Forum deleted successfully");
        fetchForms();
      }
    } catch (error: any) {
      console.error(error);
      errorToast(error?.message || "");
    }
  };

  const fetchForms = async () => {
    try {
      setIsLoading(true);
      const response = await allForms({
        page: page,
        search: debouncedSearchTerm,
        createdBy: APP_ROLE.TUTOR,
        contentType: CONTENT_TYPE.FORUM,
        ...(selectedTutor ? { userId: selectedTutor } : {}),
      }).unwrap();
      if (response.statusCode === 200) {
        setRows(response.data?.data);
        setTotalCount(response?.data?.total);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const changeStatus = async (id: any, status: any) => {
    try {
      const body = {
        status,
        ids: [id],
        type: BULK_STATUS.UPDATE,
      };
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

      const response = await updatedMulti({ body: encryptedBody }).unwrap();
      if (response?.statusCode === 200) {
        successToast("Forum status updated successfully");
        fetchForms();
      }
    } catch (error: any) {
      errorToast(error?.message);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchForms();
  }, [page, debouncedSearchTerm, selectedTutor]);

  return (
    <div>
      <Loader isLoad={isLoading} />
      <Box className="cards_header">
        <SearchBar
          value={searchTerm}
          searchTerm={searchTerm}
          onCross={() => setSearchTerm("")}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          onChange={(val: any) => {
            if (isValidInput(val.target.value)) {
              setSearchTerm(val.target.value);
            }
          }}
        />
        <Box className="cards_header_right">
          <FormControl fullWidth>
            <Select
              className="select_div"
              value={selectedTutor}
              displayEmpty
              // disabled={Boolean(_id)}
              onChange={handleTutorChange}
            >
              <MenuItem value="">Select Tutor</MenuItem>
              {tutorData?.data?.map((item: any) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.userName} ({item.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer className="table_container">
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">S.No</TableCell>
              <TableCell align="center">Image</TableCell>
              <TableCell align="center">Profile Pic</TableCell>
              <TableCell align="center">Full Name</TableCell>
              <TableCell align="center">Contact Number</TableCell>
              <TableCell align="center">Email</TableCell>
              {/* <TableCell align="center">Subject</TableCell> */}
              <TableCell align="center">Title</TableCell>
              <TableCell align="center">Views</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows?.length > 0 ? (
              rows?.map((row: any, i: number) => (
                <TableRow key={row?._id}>
                  <TableCell align="center">
                    {(page - 1) * 10 + i + 1}
                  </TableCell>
                  <TableCell>
                    <img
                      style={{ width: 100, height: 50 }}
                      src={row?.images[0] || "static/images/logo.png"}
                      alt=""
                    />
                  </TableCell>
                  <TableCell>
                    <img
                      src={
                        row?.user?.image ||
                        "/static/images/user_placeholder.png"
                      }
                      alt=""
                      style={{ width: 50, height: 50, borderRadius: "50%" }}
                    />
                  </TableCell>
                  <TableCell>{row?.user?.name || "-"}</TableCell>
                  <TableCell>
                    {row?.user?.dialCode + row?.user?.phoneNo}
                  </TableCell>
                  <TableCell>{row?.user?.email || "-"}</TableCell>
                  {/* <TableCell>{row?.subjects?.name || "-"}</TableCell> */}
                  <TableCell>{row?.title || "-"}</TableCell>
                  <TableCell>{row?.views || "-"}</TableCell>
                  <TableCell>
                    <Switch
                      checked={row?.status}
                      onChange={(e) => {
                        changeStatus(row?._id, !row?.status);
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box className="table_actions">
                      <IconButton
                        onClick={() =>
                          navigate(`/manage-forum/details/${row._id}`)
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          navigate(`/manage-forum/edit/${row._id}`)
                        }
                      >
                        <ModeEditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setOpen(true);
                          setSelectedId(row?._id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No Forum found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {rows?.length > 0 && (
        <Pagination
          setPage={setPage}
          module={rows}
          page={page}
          onPageChange={handleChangePage}
          totalPages={totalPages}
        />
      )}

      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => handleDeleteForum(selectedId)}
        name="Forum"
      />
    </div>
  );
};

export default TutorForm;
